/* Cliente HTTP base — injeta JWT + trata erros globais */
import type { ChatError } from '../types';

// URL base do backend Cloudflare Worker
// Em dev: Vite proxy redireciona /api → worker. Em prod: URL direta.
export const API_BASE =
  import.meta.env.VITE_API_BASE ?? 'https://drpera-api.luperazolo.workers.dev';

// Chave JWT no localStorage (mesma da extensão)
const JWT_KEY = 'drpera-jwt';

/** Recupera JWT salvo no localStorage */
export function getStoredJwt(): string | null {
  return localStorage.getItem(JWT_KEY);
}

/** Salva JWT no localStorage */
export function storeJwt(jwt: string): void {
  localStorage.setItem(JWT_KEY, jwt);
}

/** Remove JWT do localStorage */
export function clearJwt(): void {
  localStorage.removeItem(JWT_KEY);
}

/** Opções de fetch estendidas com tipagem */
export interface FetchOptions extends RequestInit {
  /** Se true, injeta cabeçalho Authorization com JWT ativo */
  auth?: boolean;
}

/**
 * Wrapper de fetch com Bearer JWT + tratamento de erros padronizado.
 * Lança ChatError em caso de erro HTTP.
 */
export async function fetchWithAuth(
  path: string,
  options: FetchOptions = {},
): Promise<Response> {
  const { auth = true, ...fetchOptions } = options;

  const headers = new Headers(fetchOptions.headers);
  headers.set('Content-Type', 'application/json');

  if (auth) {
    const jwt = getStoredJwt();
    if (jwt) {
      headers.set('Authorization', `Bearer ${jwt}`);
      // Mantém X-User-Id para compatibilidade com versão anterior do backend
      try {
        const payload = JSON.parse(atob(jwt.split('.')[1]));
        if (payload.sub) headers.set('X-User-Id', payload.sub);
      } catch {
        // JWT malformado — ignora silenciosamente
      }
    }
  }

  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;

  let response: Response;
  try {
    response = await fetch(url, { ...fetchOptions, headers });
  } catch (networkError) {
    const error: ChatError = {
      type: 'network',
      message: networkError instanceof Error ? networkError.message : 'Erro de rede',
    };
    throw error;
  }

  // 401 — JWT inválido ou expirado: limpa sessão
  if (response.status === 401) {
    clearJwt();
    const error: ChatError = { type: 'unauthorized', message: 'Sessão expirada' };
    throw error;
  }

  // 402 — Créditos zerados OU trial v2 encerrado (dia 4+, registrado).
  // Trial v2 (TRIAL_V2_API.md §3): mesmo HTTP 402, body { error: 'trial_ended', day }.
  // Só usuários com trial_v2 recebem esse body — pagantes seguem no fluxo atual.
  if (response.status === 402) {
    const body = (await response.json().catch(() => ({}))) as {
      error?: string;
      day?: number;
    };
    if (body.error === 'trial_ended') {
      const error: ChatError = {
        type: 'trial_ended',
        message: 'Teste gratuito encerrado',
        day: body.day,
      };
      throw error;
    }
    const error: ChatError = { type: 'credits_exhausted', message: 'Créditos esgotados' };
    throw error;
  }

  // 429 — Fair use Unlimited excedido
  if (response.status === 429) {
    const error: ChatError = { type: 'fair_use_exceeded', message: 'Limite de uso atingido' };
    throw error;
  }

  return response;
}
